import { logError } from "ErrorService";

type StorageType = "localStorage" | "sessionStorage";

/**
 * Creates a secure storage wrapper with error handling for any Storage-like object
 * @param storageType - Name of the storage type for error messages
 * @param getStorage - Function that returns the Storage object (e.g., () => window.localStorage)
 * @returns Object with secure storage operation methods
 */
const createSecureStorage = <TKey extends string>(
  storageType: StorageType,
  getStorage: () => Storage,
) => {
  /**
   * Checks if storage is available
   * @returns true if storage is available, false otherwise
   */
  const isAvailable = (): boolean =>
    typeof window !== "undefined" && !!getStorage();

  /**
   * Safely retrieves an item from storage
   * @param key - The key to retrieve
   * @returns The stored value or null if retrieval fails
   */
  const getItem = (key: TKey): string | null => {
    try {
      if (!isAvailable()) {
        throw new Error(`${storageType} is not available`);
      }
      return getStorage().getItem(key);
    } catch (error) {
      logError(
        error instanceof Error
          ? error
          : new Error(`Failed to get ${storageType} item with key "${key}"`),
        { key, originalError: error, storageType },
        false,
      );
      return null;
    }
  };

  /**
   * Safely sets an item in storage
   * @param key - The key to set
   * @param value - The value to store
   */
  const setItem = (key: TKey, value: string): void => {
    try {
      if (!isAvailable()) {
        throw new Error(`${storageType} is not available`);
      }
      getStorage().setItem(key, value);
    } catch (error) {
      logError(
        error instanceof Error
          ? error
          : new Error(`Failed to set ${storageType} item with key "${key}"`),
        { key, value, originalError: error, storageType },
        false,
      );
    }
  };

  /**
   * Safely removes an item from storage
   * @param key - The key to remove
   */
  const removeItem = (key: TKey): void => {
    try {
      if (!isAvailable()) {
        throw new Error(`${storageType} is not available`);
      }
      getStorage().removeItem(key);
    } catch (error) {
      logError(
        error instanceof Error
          ? error
          : new Error(`Failed to remove ${storageType} item with key "${key}"`),
        { key, originalError: error, storageType },
        false,
      );
    }
  };

  /**
   * Safely clears all items from storage
   */
  const clear = (): void => {
    try {
      if (!isAvailable()) {
        throw new Error(`${storageType} is not available`);
      }
      getStorage().clear();
    } catch (error) {
      logError(
        error instanceof Error
          ? error
          : new Error(`Failed to clear ${storageType}`),
        { originalError: error, storageType },
        false,
      );
    }
  };

  /**
   * Safely retrieves and parses a JSON object from storage
   * @param key - The key to retrieve
   * @returns The parsed object or null if retrieval/parsing fails
   */
  const getJSON = <T>(key: TKey): T | null => {
    const item = getItem(key);
    if (!item) {
      return null;
    }

    try {
      return JSON.parse(item) as T;
    } catch (error) {
      logError(
        new Error(`Failed to parse ${storageType} JSON for key "${key}"`),
        { key, value: item, originalError: error, storageType },
        false,
      );
      return null;
    }
  };

  /**
   * Safely stringifies and stores a JSON object in storage
   * @param key - The key to set
   * @param value - The object to store
   */
  const setJSON = <T>(key: TKey, value: T): void => {
    try {
      const serialized = JSON.stringify(value);
      setItem(key, serialized);
    } catch (error) {
      logError(
        new Error(`Failed to stringify ${storageType} JSON for key "${key}"`),
        { key, value, originalError: error, storageType },
        false,
      );
    }
  };

  return {
    isAvailable,
    getItem,
    setItem,
    removeItem,
    clear,
    getJSON,
    setJSON,
  };
};

export default createSecureStorage;
